import './left-nav.scss';
import { NavLink, useLocation } from 'react-router-dom';
import Icon from '../../components/icons/icon';
import { useAdmin } from '../../hooks/use-admin-context';
import { Constants } from '../../constants/constants';
import React, { forwardRef, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Typography } from '../../components/typography/typography';
import { ActionWidget } from '../action-widget/action-widget';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { Button } from '../../components/form/button';
import { CiCalculator1 } from 'react-icons/ci';
import { TenantContext } from '../../context/tenant-context';
import useRememberQueryParams from '../../hooks/use-remember-query-params';
import { useResponsive } from '../../hooks/use-reponsive';

interface NavItem extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  label?: string;
  link?: string;
  absoluteLink?: string;
  icon?: React.ReactElement;
  showSubMenu?: boolean;
  legacyLink?: boolean;
  openSubmenu?: string | null;
  toggleSubmenu?: (label: string) => void;
}

const NavItem = ({
  className = '',
  label = '',
  link,
  absoluteLink,
  icon,
  legacyLink = false,
  children,
  onClick,
  openSubmenu,
  toggleSubmenu,
}: React.PropsWithChildren<NavItem>) => {
  const { contextPath } = useContext(TenantContext);
  const location = useLocation();
  const queryParams = useRememberQueryParams(link || '');
  const path = `/manager/${link || ''}`;

  return (
    <li>
      {link ? (
        !legacyLink ? (
          <NavLink
            end
            className={({ isActive }) => classNames('navitem', { active: isActive }, className)}
            onClick={(e) => {
              onClick && onClick(e);
            }}
            to={{
              pathname: `${contextPath}manager/${link || ''}`,
              search: queryParams?.toString(),
            }}
          >
            {icon} <Typography type="text-sm">{label}</Typography>
          </NavLink>
        ) : (
          <a
            href={`${contextPath}manager/${link || ''}`}
            className={classNames('navitem', { active: path === location.pathname }, className)}
            onClick={(e) => {
              onClick && onClick(e);
            }}
          >
            {/** TODO: Change this <a> component to <NavLink> after all Legacy application has been migrated  */}
            {icon} <Typography type="text-sm">{label}</Typography>
          </a>
        )
      ) : (
        <a
          href={absoluteLink ?? '#'}
          target={absoluteLink ? '_blank' : undefined}
          className={classNames('navitem', className)}
          onClick={(e) => {
            !absoluteLink && e.preventDefault(); // stop navigation
            toggleSubmenu?.(label); // open/close submenu
            onClick && onClick(e);
          }}
        >
          {icon} <Typography type="text-sm">{label}</Typography>
        </a>
      )}
      {children && (
        <ul
          id={`nav-${label.toLowerCase()}`}
          className={classNames('nav-sub-ul', { 'show-submenu': openSubmenu === label })}
        >
          {children}
        </ul>
      )}
    </li>
  );
};

const NavMenu = ({ children }: React.PropsWithChildren) => {
  return <ul className="nav-list">{children}</ul>;
};

export const LeftNav = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
}) => {
  const { name, hasPermissions, contextPath } = useAdmin();
  const { isMobileOrTablet } = useResponsive();

  const [isHover, setIsHover] = useState<boolean>(false);
  const lastTimeOut = useRef<NodeJS.Timeout>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const { getMultipleConfigurations } = useAPI();
  const [configurations, setConfigurations] = useState<Configuration[]>([]);

  const toggleSubmenu = (label: string) => {
    setOpenSubmenu((prev) => (prev === label ? null : label));
  };

  const getConfiguration = useCallback(
    (variable: string) => configurations.find((config) => config.variable === variable)?.value,
    [configurations],
  );

  const navBarRef = useRef<HTMLElement>(null);

  const closeNavs = (e: CustomEventInit) => {
    const subMenus = navBarRef.current?.querySelectorAll('.nav-sub-ul');
    subMenus?.forEach((elem) => {
      if (elem.id === e.detail) {
        if (elem.classList.contains('show-submenu')) {
          elem.classList.remove('show-submenu');
        } else {
          elem.classList.add('show-submenu');
        }
      } else {
        elem.classList.remove('show-submenu');
      }
    });
  };

  const obtainConfigurations = () => {
    getMultipleConfigurations([
      'CONF_SITE_TAX_OPTIONS',
      'CONF_SITE_DELIVERY_OPTIONS',
      'CONF_ALLOW_SALE',
      'CONF_RENTER_TAG_PRICING',
      'CONF_RENT_ON_ACCOUNT',
      'CONF_WEBSITE_NAME',
      'CONF_FAVICON',
      'CONF_STRIPE_ACCOUNT_TYPE',
      'CONF_ACCOUNTING_BETA',
      'CONF_ACCOUNTING_PERIOD_CLOSE',
      'CONF_TAX_EXEMPT_REPORT',
      'CONF_DELIVERY_REVENUE_REPORT',
      'CONF_ENABLE_SERVICE_AND_REPAIR',
      'CONF_COMMISSION_ENABLED',
      'CONF_DASHBOARD_V2',
      'CONF_ENABLE_PURCHASE_ORDERS',
      'CONF_INSPECTIONS_ENABLED',
      'CONF_ENABLE_CREDIT_MEMO',
    ])
      .then((response) => {
        setConfigurations(response);

        // Set web page title
        const title = response.find((elem) => elem.variable === 'CONF_WEBSITE_NAME')?.value || '';
        const favicon = response.find((elem) => elem.variable === 'CONF_FAVICON')?.value || '';

        document.title = `${title} Control Panel`;

        // Set favicon
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        const path = window.location.href.split('/manager')[0];
        if (!link) {
          link = document.createElement('link');
          link.rel = 'shortcut icon';
          document.head.appendChild(link);
        }
        link.href = `${path}/image/site_favicon/${favicon}`;
      })
      .catch(() => {
        setConfigurations([]);
      });
  };

  const updateLeftNav = useCallback(() => {
    obtainConfigurations();
  }, []);

  useEffect(() => {
    if (navBarRef.current) {
      navBarRef.current.removeEventListener('close-navs', closeNavs);
      navBarRef.current?.addEventListener('close-navs', closeNavs);
    }
    return () => {
      navBarRef.current && navBarRef.current.removeEventListener('close-navs', closeNavs);
    };
  }, [navBarRef]);

  useEffect(() => {
    obtainConfigurations();
    document.body.addEventListener('update-left-nav', updateLeftNav);
    return () => {
      document.body.removeEventListener('update-left-nav', updateLeftNav);
    };
  }, []);

  const getMenuItems = (onClickLink?: () => void) => (
    <>
      <div className="nav-header">
        {isMobileOrTablet ? (
          <>
            <a
              href="#"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <Icon icon="Close" size="medium" stroke="var(--quipli-white-50)" />
            </a>
          </>
        ) : (
          <>
            <a
              className={classNames('collapse-icon', { active: !isCollapsed })}
              href="#"
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              <Icon icon="SideBar" stroke="var(--grey-400)" />
            </a>
            <div className={classNames({ hidden: isCollapsed && !isHover })}>
              <Icon icon="QuipliLogo" size="xlarge" fill="var(--quipli-white-50)" />
            </div>
          </>
        )}
      </div>
      <NavMenu>
        {(hasPermissions(Constants.Permissions.Dashboard_Sales_Revenue) ||
          hasPermissions(Constants.Permissions.Dashboard_Orders_Products)) && (
          <NavItem
            onClick={onClickLink}
            label={'Dashboard'}
            link="home"
            icon={<Icon icon="Dashboard" stroke="var(--quipli-white-50)" />}
          />
        )}

        {/* Orders */}
        {(hasPermissions(Constants.Permissions.Customer_Order) ||
          hasPermissions(Constants.Permissions.Extend_Call_Off_Requests)) && (
          <NavItem
            label={'Orders'}
            icon={<Icon icon="ShoppingBag" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            {hasPermissions(Constants.Permissions.Customer_Order) && (
              <>
                <NavItem onClick={onClickLink} label={'Orders'} link="orders" />
                <NavItem onClick={onClickLink} label={'Create Order'} link="orders/create" />
                <NavItem onClick={onClickLink} label={'Create Missed Rental'} link="orders/createMissed" />
                <NavItem onClick={onClickLink} label={'Create Quote'} link="orders/create_quote" />
              </>
            )}
            {hasPermissions(Constants.Permissions.Extend_Call_Off_Requests) && (
              <NavItem onClick={onClickLink} label={'Extension/Call-Off Requests'} link="orderrefundshrink" />
            )}
          </NavItem>
        )}

        {hasPermissions(Constants.Permissions.Customer_Order) && (
          <NavItem
            onClick={onClickLink}
            label={'Dispatch'}
            link="dispatch"
            icon={<Icon icon="Truck" stroke="var(--quipli-white-50)" />}
          />
        )}

        {/* Users */}
        {hasPermissions(Constants.Permissions.Customers_Users) && (
          <NavItem
            label={'Customers'}
            icon={<Icon icon="Customer" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            <NavItem onClick={onClickLink} label={'Manage Customers'} link="users" />
            {getConfiguration('CONF_RENT_ON_ACCOUNT') === '1' && (
              <NavItem onClick={onClickLink} label={'On Account Requests'} link="users/account_requests" />
            )}
            <NavItem onClick={onClickLink} label={'Customer Reviews'} link="reviews" />
            {getConfiguration('CONF_RENTER_TAG_PRICING') === '1' && (
              <NavItem onClick={onClickLink} label={'Customer Tag Pricing'} link="tagpricing" />
            )}
            <NavItem onClick={onClickLink} label={'Representative Roles'} link="representatives/roles" />
          </NavItem>
        )}

        {/* Inventory */}
        {(hasPermissions(Constants.Permissions.Manage_Products) ||
          hasPermissions(Constants.Permissions.Inventory_Calendar) ||
          hasPermissions(Constants.Permissions.Product_Categories)) && (
          <NavItem
            label={'Inventory'}
            icon={<Icon icon="Inventory" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            {hasPermissions(Constants.Permissions.Manage_Products) && (
              <>
                <NavItem onClick={onClickLink} label={'Products'} link="products" />
                <NavItem onClick={onClickLink} label={'Product Units'} link="productUnits" />
                <NavItem onClick={onClickLink} label={'Parts'} link="inventory/parts" />
                <NavItem onClick={onClickLink} label={'Services'} link="inventory/services" />
              </>
            )}
            {hasPermissions(Constants.Permissions.Inventory_Calendar) && (
              <NavItem onClick={onClickLink} label={'Inventory Calendar'} link="calendar" />
            )}
            {hasPermissions(Constants.Permissions.Product_Categories) && (
              <NavItem onClick={onClickLink} label={'Categories'} link="categories" />
            )}
          </NavItem>
        )}

        {/* Purchase Orders */}
        {getConfiguration('CONF_ENABLE_PURCHASE_ORDERS') === '1' && (
          <NavItem
            label={'Purchase Orders'}
            icon={<Icon icon="ShoppingBag2" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            {/* TODO add permission */}
            <NavItem onClick={onClickLink} label={'Orders'} link="purchase-orders" />
            <NavItem onClick={onClickLink} label={'Create Order'} link="purchase-orders/create" />
            <NavItem onClick={onClickLink} label={'Manage Vendors'} link="vendors" />
          </NavItem>
        )}

        {/* Service & Repair */}
        {getConfiguration('CONF_ENABLE_SERVICE_AND_REPAIR') === '1' &&
          hasPermissions(Constants.Permissions.Work_Orders) && (
            <NavItem
              label={'Service & Repair'}
              icon={<Icon icon="Tool02" stroke="var(--quipli-white-50)" />}
              openSubmenu={openSubmenu}
              toggleSubmenu={toggleSubmenu}
            >
              <NavItem onClick={onClickLink} label={'Work Orders'} link="repair/work-orders" />
              <NavItem onClick={onClickLink} label={'Scheduled Services'} link="repair/scheduled-services" />
            </NavItem>
          )}

        {/* Accounting */}
        {(hasPermissions(Constants.Permissions.Collectibles) ||
          hasPermissions(Constants.Permissions.Accounting) ||
          hasPermissions(Constants.Permissions.Quick_Books_Online) ||
          getConfiguration('CONF_SITE_TAX_OPTIONS') === '5') && (
          <NavItem
            label={'Accounting'}
            icon={<CiCalculator1 size={22} />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            {hasPermissions(Constants.Permissions.Collectibles) && (
              <NavItem onClick={onClickLink} label={'Collectibles'} link="collectibles" />
            )}
            {hasPermissions(Constants.Permissions.Accounting) && (
              <>
                {getConfiguration('CONF_ACCOUNTING_BETA') === '1' && (
                  <>
                    <NavItem onClick={onClickLink} label={'General Ledger'} link="accounting/accounts" />
                    <NavItem onClick={onClickLink} label={'General Ledger Settings'} link="accounting/settings" />
                    {getConfiguration('CONF_ACCOUNTING_PERIOD_CLOSE') === '1' && (
                      <NavItem onClick={onClickLink} label={'Accounting Periods'} link="accounting/accountingPeriods" />
                    )}
                  </>
                )}
                <NavItem onClick={onClickLink} label={'Daily Payment Totals'} link="reports/dailyPayments" />
                <NavItem onClick={onClickLink} label={'Tax'} link="reports/tax" />
                {getConfiguration('CONF_TAX_EXEMPT_REPORT') === '1' && (
                  <NavItem onClick={onClickLink} label={'Tax Exempt'} link="reports/tax_exempt" />
                )}
                <NavItem onClick={onClickLink} label={'Payments'} link="reports/payments" />
                {getConfiguration('CONF_STRIPE_ACCOUNT_TYPE') === 'custom' && (
                  <>
                    <NavItem label={'Stripe Payments'} link="quipli-pay/payments" />
                    <NavItem onClick={onClickLink} label={'Payouts'} link="quipli-pay/payouts" />
                  </>
                )}
                <NavItem
                  onClick={onClickLink}
                  label={'Payout Reconciliation'}
                  link="reports/stripePayoutReconciliationReport"
                />
                <NavItem onClick={onClickLink} label={'Payment Method Configuration'} link="payment-config/methods" />
              </>
            )}
            {getConfiguration('CONF_SITE_TAX_OPTIONS') === '5' && (
              <NavItem onClick={onClickLink} label={'Tax Codes'} link="taxcodes" />
            )}
            {hasPermissions(Constants.Permissions.Quick_Books_Online) && (
              <NavItem onClick={onClickLink} label={'QuickBooks Online Setup'} link="qboaccount" />
            )}
          </NavItem>
        )}

        {getConfiguration('CONF_ENABLE_CREDIT_MEMO') === '1' && (
          <NavItem
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
            label={'Credit Memos'}
            icon={<Icon icon="File" stroke="var(--quipli-white-50)" />}
          >
            <NavItem onClick={onClickLink} label={'Create Credit Memo'} link="orders/createCreditMemo" />
            <NavItem onClick={onClickLink} label={'Credit Memos'} link="reports/creditMemos" />
          </NavItem>
        )}

        {/* Reports */}
        {hasPermissions(Constants.Permissions.Reports) && (
          <NavItem
            label={'Reports'}
            icon={<Icon icon="Report" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            <NavItem onClick={onClickLink} label={'Daily Schedule'} link="reports/dailySchedule" />
            <NavItem onClick={onClickLink} label={'Transactions'} link="reports/transactions" />
            {getConfiguration('CONF_DELIVERY_REVENUE_REPORT') === '1' && (
              <NavItem onClick={onClickLink} label={'Delivery Revenue'} link="reports/deliveryRevenue" />
            )}
            <NavItem onClick={onClickLink} label={'Renters'} link="reports/users" />
            <NavItem onClick={onClickLink} label={'Customer Searches'} link="reports/customer-searches" />
            <NavItem onClick={onClickLink} label={'Products'} link="reports/products" />
            {getConfiguration('CONF_DASHBOARD_V2') === '1' && (
              <NavItem onClick={onClickLink} label={'Revenue'} link="reports/revenue" />
            )}
            {hasPermissions(Constants.Permissions.Staff_Members) &&
              getConfiguration('CONF_COMMISSION_ENABLED') === '1' && (
                <NavItem onClick={onClickLink} label={'Sales Commission'} link="reports/salesCommission" />
              )}
            <NavItem onClick={onClickLink} label={'Missed Rentals'} link="reports/missedRentals" />
          </NavItem>
        )}

        {/* Customizations */}
        {(hasPermissions(Constants.Permissions.Navigations) ||
          hasPermissions(Constants.Permissions.Content_Pages) ||
          hasPermissions(Constants.Permissions.Content_Block) ||
          hasPermissions(Constants.Permissions.Language_Labels) ||
          hasPermissions(Constants.Permissions.FAQ_Categories) ||
          hasPermissions(Constants.Permissions.FAQ_Management) ||
          hasPermissions(Constants.Permissions.Cancel_Reasons) ||
          hasPermissions(Constants.Permissions.Discount_Coupons) ||
          hasPermissions(Constants.Permissions.Delivery_Locations) ||
          hasPermissions(Constants.Permissions.Social_Platforms_Management) ||
          getConfiguration('CONF_SITE_DELIVERY_OPTIONS') === '3') && (
          <NavItem
            label={'Customizations'}
            icon={<Icon icon="Customizations" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            {hasPermissions(Constants.Permissions.Navigations) && (
              <NavItem onClick={onClickLink} label={'Web Navigations'} link="navigations" />
            )}
            {hasPermissions(Constants.Permissions.Content_Pages) && (
              <NavItem onClick={onClickLink} label={'Content Pages'} link="cms" />
            )}
            {hasPermissions(Constants.Permissions.Content_Block) && (
              <NavItem onClick={onClickLink} label={'Content Block'} link="extrapage" />
            )}
            {hasPermissions(Constants.Permissions.Custom_Agree_Types) && (
              <NavItem onClick={onClickLink} label={'Custom Agree Types'} link="customagreetypes" />
            )}
            {hasPermissions(Constants.Permissions.Discount_Coupons) && (
              <NavItem onClick={onClickLink} label={'Discount Coupons'} link="coupons" />
            )}
            {hasPermissions(Constants.Permissions.FAQ_Categories) && (
              <NavItem onClick={onClickLink} label={'FAQ Category Management'} link="faqcategories" />
            )}
            {hasPermissions(Constants.Permissions.FAQ_Management) && (
              <NavItem onClick={onClickLink} label={'FAQ Management'} link="faqs" />
            )}
            {hasPermissions(Constants.Permissions.Cancel_Reasons) && (
              <NavItem onClick={onClickLink} label={'Cancel Reasons'} link="cancelreasons" />
            )}
            {hasPermissions(Constants.Permissions.Language_Labels) && (
              <NavItem onClick={onClickLink} label={'Labels Text'} link="labels" />
            )}
            {hasPermissions(Constants.Permissions.Social_Platforms_Management) && (
              <NavItem onClick={onClickLink} label={'Social Platforms'} link="socialmedia" />
            )}
            {hasPermissions(Constants.Permissions.Delivery_Locations) && (
              <NavItem onClick={onClickLink} label={'Delivery Location'} link="deliverylocations" />
            )}
            {getConfiguration('CONF_SITE_DELIVERY_OPTIONS') === '3' && (
              <NavItem onClick={onClickLink} label={'Zip Delivery Rates'} link="zipdelivery" />
            )}
          </NavItem>
        )}

        {/* Settings */}
        {(hasPermissions(Constants.Permissions.General_Settings) ||
          hasPermissions(Constants.Permissions.Currency_Management) ||
          hasPermissions(Constants.Permissions.Manage_Shops) ||
          hasPermissions(Constants.Permissions.Location_Attributes) ||
          hasPermissions(Constants.Permissions.Product_Attributes) ||
          hasPermissions(Constants.Permissions.Product_Options) ||
          hasPermissions(Constants.Permissions.Filters) ||
          hasPermissions(Constants.Permissions.Theme_Settings) ||
          hasPermissions(Constants.Permissions.Manage_Email_Templates) ||
          hasPermissions(Constants.Permissions.Bulk_Import_Export)) && (
          <NavItem
            label={'Settings'}
            icon={<Icon icon="Settings" stroke="var(--quipli-white-50)" />}
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            {hasPermissions(Constants.Permissions.General_Settings) && (
              <NavItem onClick={onClickLink} label={'General Settings'} link="configurations" />
            )}
            {getConfiguration('CONF_STRIPE_ACCOUNT_TYPE') !== 'custom' &&
              hasPermissions(Constants.Permissions.Currency_Management) && (
                <NavItem onClick={onClickLink} label={'Stripe Setup'} link="quipli-pay" />
              )}
            {getConfiguration('CONF_STRIPE_ACCOUNT_TYPE') === 'custom' &&
              hasPermissions(Constants.Permissions.Currency_Management) && (
                <>
                  <NavItem onClick={onClickLink} label={'Stripe Setup'} link="quipli-pay" />
                  <NavItem onClick={onClickLink} label={'Card Readers'} link="quipli-pay/terminal" />
                  <NavItem onClick={onClickLink} label={'Bank'} link="quipli-pay/bank" />
                </>
              )}
            {hasPermissions(Constants.Permissions.Manage_Shops) && (
              <NavItem onClick={onClickLink} label={'Locations'} link="shops" />
            )}
            {hasPermissions(Constants.Permissions.Location_Attributes) && (
              <NavItem onClick={onClickLink} label={'Location Fees'} link="shopattributes" />
            )}
            {hasPermissions(Constants.Permissions.Product_Attributes) && (
              <NavItem onClick={onClickLink} label={'Product Fees'} link="productfeesattributes" />
            )}
            {hasPermissions(Constants.Permissions.Product_Options) && (
              <>
                <NavItem onClick={onClickLink} label={'Product Options'} link="options/admin" />
                {getConfiguration('CONF_ALLOW_SALE') === '1' && (
                  <NavItem onClick={onClickLink} label={'Custom Product Builder'} link="services" />
                )}
              </>
            )}
            {hasPermissions(Constants.Permissions.Filters) && (
              <NavItem onClick={onClickLink} label={'Filters'} link="filters" />
            )}
            {hasPermissions(Constants.Permissions.Theme_Settings) && (
              <NavItem onClick={onClickLink} label={'Themes Settings'} link="themes" />
            )}
            {hasPermissions(Constants.Permissions.Manage_Email_Templates) && (
              <NavItem onClick={onClickLink} label={'Email Customizations'} link="emailtemplatesv2" />
            )}
            {hasPermissions(Constants.Permissions.Bulk_Import_Export) && (
              <NavItem onClick={onClickLink} label={'Export / Import'} link="importexport/default_action/export" />
            )}
            {hasPermissions(Constants.Permissions.General_Settings) &&
              getConfiguration('CONF_INSPECTIONS_ENABLED') === '1' && (
                <NavItem onClick={onClickLink} label={'Inspection Workflows'} link="inspectionWorkflows" />
              )}
          </NavItem>
        )}
      </NavMenu>

      <div className="nav-footer">
        <NavMenu>
          {hasPermissions(Constants.Permissions.Staff_Members) && (
            <NavItem
              onClick={onClickLink}
              label={'Admin Users'}
              link="admins"
              icon={<Icon icon="AddCustomer" stroke="var(--quipli-white-50)" />}
            />
          )}
          <NavItem
            onClick={onClickLink}
            icon={<Icon icon="HelpCircle" stroke="var(--quipli-white-50)" />}
            label={'Quipli Help Center'}
            absoluteLink="https://quiplihelp.zendesk.com/hc/en-us/"
          />
          <NavItem
            label={'Support'}
            icon={<Icon icon="Support" stroke="var(--quipli-white-50)" />}
            onClick={() => {
              onClickLink?.();
              window.zE.activate({ hideOnClose: true });
            }}
          />
          <NavItem
            onClick={onClickLink}
            icon={<Icon icon="Store" fill="var(--quipli-white-50)" strokeWidth={1} />}
            label={'Quipli Marketplace'}
            absoluteLink="https://marketplace.quipli.com/"
          />
          <NavItem
            onClick={onClickLink}
            icon={<Icon icon="Refer" />}
            label={'Refer and Earn'}
            absoluteLink="https://quipli.com/refer/"
          />
          <NavItem
            className="profile"
            label={name}
            icon={
              <img
                src={`${contextPath}image/user/small`}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '200px',
                }}
              />
            }
            openSubmenu={openSubmenu}
            toggleSubmenu={toggleSubmenu}
          >
            <NavItem onClick={onClickLink} label={'Profile'} link="admin/profile" />
            <NavItem onClick={onClickLink} label={'Change Password'} link="admin/change_password" />
            <NavItem legacyLink={true} onClick={onClickLink} label={'Log Out'} link="admin/logout" />
          </NavItem>
        </NavMenu>
      </div>
    </>
  );

  const onNavChange = (value: boolean) => {
    clearTimeout(lastTimeOut.current);
    lastTimeOut.current = setTimeout(() => {
      setIsHover(value);
    }, 200);
  };

  return (
    <>
      {isMobileOrTablet ? (
        <MobileLeftNav ref={navBarRef} isOpen={isOpen} setIsOpen={setIsOpen}>
          {getMenuItems(() => setIsOpen(false))}
        </MobileLeftNav>
      ) : (
        <DesktopLeftNav ref={navBarRef} isHover={isHover} isCollapsed={isCollapsed} onNavChange={onNavChange}>
          {getMenuItems()}
        </DesktopLeftNav>
      )}
    </>
  );
};

type DesktopLeftNavProps = PropsWithChildren<{
  isHover: boolean;
  isCollapsed: boolean;
  onNavChange: (v: boolean) => void;
}>;

const DesktopLeftNav = forwardRef<HTMLElement, DesktopLeftNavProps>(
  ({ isHover, isCollapsed, onNavChange, children }, ref) => {
    return (
      <nav
        ref={ref}
        onMouseEnter={() => isCollapsed && onNavChange(true)}
        onMouseLeave={() => isCollapsed && onNavChange(false)}
        className={classNames('left-nav', {
          collapsed: isCollapsed,
          hover: isCollapsed && isHover,
          'not-hover': isCollapsed && !isHover,
        })}
      >
        {children}
      </nav>
    );
  },
);

type MobileLeftNavProps = PropsWithChildren<{
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}>;

const MobileLeftNav = forwardRef<HTMLElement, MobileLeftNavProps>(({ isOpen, setIsOpen, children }, ref) => {
  return (
    <nav ref={ref} className={classNames('left-nav-mobile')}>
      <div className="navbar">
        <div>
          <a
            href="#"
            onClick={() => {
              setIsOpen(true);
            }}
          >
            <Icon icon="HamburguerMenu" stroke="var(--grey-400)" />
          </a>
          <Icon icon="QuipliLogo" size="xlarge" fill="var(--quipli-white-50)" />
        </div>
        <div>
          <Button
            className="button-tertiary"
            onClick={() => {
              window.open(window.location.href.split('/manager')[0], 'blank');
            }}
          >
            <Icon icon="World" />
          </Button>
          <ActionWidget className="header" />
        </div>
      </div>
      <div
        className={classNames('navbar-background', { hidden: !isOpen })}
        onClick={() => {
          setIsOpen(false);
        }}
      ></div>
      <div className={classNames('navbar-content', { hidden: !isOpen })}>{children}</div>
    </nav>
  );
});
